/**
 * PortalInviteOverview — hub/overview page for a single invite.
 * Mirrors PortalProjectDetail as the landing page when a user opens an invite.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { PageHeader, SectionCard, StatusPill, InviteTabBar } from '@/components/portal/PortalUI';
import {
  ClipboardList, MapPin, Calendar, BadgeCheck,
  FileText, ShieldCheck, Clock, CheckCircle2, BookOpen,
  AlertCircle, ListChecks,
} from 'lucide-react';
import { supabase } from '@shared/supabaseClient';

const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return null; }
};

const statusInfo = (status) => {
  const map = {
    approved:               { label: 'Approved',         tone: 'success', icon: CheckCircle2,  desc: 'Your induction is approved and your digital pass is active.' },
    submitted:              { label: 'Submitted',         tone: 'warning', icon: Clock,         desc: 'Submitted — awaiting team leader review.' },
    pending_acknowledgement:{ label: 'Re-confirm Needed', tone: 'info',    icon: BookOpen,      desc: 'Please review and re-confirm this induction.' },
    in_progress:            { label: 'In Progress',       tone: 'info',    icon: ListChecks,    desc: 'Induction started — continue where you left off.' },
    invited:                { label: 'Not Started',       tone: 'neutral', icon: ClipboardList, desc: 'Complete this induction to receive your digital badge.' },
  };
  return map[status] || { label: status, tone: 'info', icon: ClipboardList, desc: '' };
};

const PortalInviteOverview = () => {
  const { submissionId } = useParams();
  const { session, contextInduction } = useOutletContext();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInvite();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId, session]);

  const loadInvite = async () => {
    const email = session?.person?.email || session?.email;
    if (!email || !submissionId) { setLoading(false); return; }

    setLoading(true);
    try {
      // Use the same RPC the list uses so we get consistent data
      const { data, error: rpcErr } = await supabase.rpc('portal_get_all_invites_by_email', { p_email: email });
      if (rpcErr) throw rpcErr;

      const match = (data || []).find(r => r.submission_id === submissionId);
      if (!match) {
        // Fallback: directly query the submission
        const { data: sub } = await supabase
          .from('induction_submissions')
          .select('id, person_name, person_email, status, public_reference, created_at, valid_from, depot_id, depots:depot_id(name)')
          .eq('id', submissionId)
          .maybeSingle();
        if (sub) {
          setInvite({
            submission_id: sub.id,
            title: 'Site Induction',
            status: sub.status,
            depot_name: sub.depots?.name || null,
            public_reference: sub.public_reference,
            valid_from: sub.valid_from,
            created_at: sub.created_at,
          });
        } else {
          setError('Invite not found.');
        }
      } else {
        setInvite(match);
      }
    } catch (err) {
      console.error('Error loading invite:', err);
      setError('Failed to load invite details.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
        <PageHeader title="Invite Overview" />
        <div className="text-center py-12 text-muted-foreground">Loading invite details...</div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="space-y-6">
        <InviteTabBar submissionId={submissionId} inviteStatus={contextInduction?.status} />
        <PageHeader title="Invite Overview" />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error || 'Invite not found.'}</p>
        </div>
      </div>
    );
  }

  const info = statusInfo(invite.status);
  const Icon = info.icon;
  const visitDate = fmtDate(invite.valid_from) || fmtDate(invite.created_at);
  const isComplete = invite.status === 'approved';

  return (
    <div className="space-y-6">
      <InviteTabBar submissionId={submissionId} inviteStatus={invite.status} />
      <PageHeader
        title={invite.title || 'Site Induction'}
        subtitle={invite.depot_name || 'Site Visit Invite'}
      />

      {/* Status banner */}
      <SectionCard>
        <div className="flex items-start gap-3">
          <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <StatusPill tone={info.tone} label={info.label} />
              {visitDate && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {visitDate}
                </span>
              )}
              {invite.depot_name && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {invite.depot_name}
                </span>
              )}
              {invite.public_reference && (
                <span className="text-xs text-muted-foreground">
                  Ref: {invite.public_reference}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{info.desc}</p>
          </div>
        </div>
      </SectionCard>

      {/* Quick links */}
      <SectionCard title="What would you like to do?">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => navigate(`/portal/inductions/${submissionId}/setup`)}
            className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
          >
            <ClipboardList className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Setup Guide</p>
              <p className="text-xs text-muted-foreground">View your onboarding steps</p>
            </div>
          </button>

          <button
            onClick={() => navigate(`/portal/inductions/${submissionId}/documents`)}
            className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
          >
            <FileText className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">Documents</p>
              <p className="text-xs text-muted-foreground">View site documents</p>
            </div>
          </button>

          {isComplete && (
            <button
              onClick={() => navigate(`/portal/digital-badge?submission=${submissionId}`)}
              className="flex items-center gap-3 p-4 rounded-lg border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
            >
              <BadgeCheck className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Digital Badge</p>
                <p className="text-xs text-muted-foreground">View your site pass</p>
              </div>
            </button>
          )}
        </div>
      </SectionCard>

      {/* CTA — removed: overview is only shown for completed invites */}
    </div>
  );
};

export default PortalInviteOverview;
